import {
  Component,
  ElementRef,
  input,
  InputSignal,
  Renderer2,
  OnDestroy,
  effect,
  computed,
  Signal,
  ChangeDetectionStrategy,
  signal,
  WritableSignal,
  inject,
  viewChild,
  DOCUMENT,
} from '@angular/core';
import { ContentComponent } from '@lyceo/shared/ui';
import { SidebarLayoutService } from './sidebar-layout-service';
import { Observable } from 'rxjs';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faCircleChevronRight,
  faCircleChevronLeft,
  IconDefinition,
} from '@fortawesome/pro-light-svg-icons';
import { AsyncPipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lyceo-sidebar-layout',
  templateUrl: './sidebar-layout.component.html',
  styleUrls: ['./sidebar-layout.component.scss'],
  imports: [ContentComponent, AsyncPipe, FaIconComponent],
  host: {
    '[class.collapsed]': 'isCollapsed',
    '(window:resize)': 'onResize()',
  },
})
export class SidebarLayoutComponent implements OnDestroy {
  private readonly document: Document = inject<Document>(DOCUMENT);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly sidebarLayoutService: SidebarLayoutService =
    inject(SidebarLayoutService);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);

  public readonly sidebar: Signal<ElementRef | undefined> = viewChild<
    ElementRef | undefined
  >('sidebar');

  public readonly background: InputSignal<boolean> = input<boolean>(false);
  public readonly alternate: InputSignal<boolean> = input<boolean>(false);
  public readonly maxWidth: InputSignal<boolean> = input<boolean>(true);
  public readonly isMobileScreenSize: Signal<boolean | undefined> =
    this.applicationFacade.isMobileScreenSize;
  public sidebarOpen$: Observable<boolean> =
    this.sidebarLayoutService.sidebarOpen$;
  public readonly headerHeight: InputSignal<100 | 114> = input<100 | 114>(100);
  protected readonly faCircleChevronRight: IconDefinition =
    faCircleChevronRight;
  protected readonly faCircleChevronLeft: IconDefinition = faCircleChevronLeft;
  public isCollapsed: boolean = false;

  private isDragging: boolean = false;
  private isOpen: boolean = false;
  private startY: number = 0;
  private currentY: number = 0;
  private startTime: number = 0;
  //sidebar leaves this amount of space at the top in percentage of total page on mobile
  public readonly topLimit: number = 10;

  private readonly windowHeight: WritableSignal<number> = signal(
    window.innerHeight,
  );
  public readonly calcHeaderHeight: Signal<number> = computed(() => {
    const menuHeight: number =
      this.document.querySelector('lyceo-menu')?.clientHeight || 0;
    return (
      100 - (this.headerHeight() / (this.windowHeight() - menuHeight)) * 100
    );
  });

  constructor() {
    effect(() => {
      const sidebar: ElementRef | undefined = this.sidebar();
      if (!this.isMobileScreenSize() && sidebar && sidebar?.nativeElement) {
        this.renderer.removeStyle(sidebar.nativeElement, 'transition');
      }
    });

    this.sidebarOpen$.subscribe((open) => {
      this.isOpen = open;
      this.updateSidebarMobile(true);
    });
  }

  public onResize(): void {
    this.windowHeight.set(window.innerHeight);
    this.updateSidebarMobile(false);
  }

  public updateSidebarMobile(transition: boolean): void {
    if (this.isMobileScreenSize()) {
      const sidebarElement: HTMLElement | undefined =
        this.sidebar()?.nativeElement;
      if (sidebarElement) {
        const translateY: string = this.isOpen
          ? `${this.topLimit}%`
          : `${this.calcHeaderHeight()}%`;
        if (transition) {
          this.renderer.setStyle(
            sidebarElement,
            'transition',
            'transform 0.3s ease-in-out',
          );
        } else {
          this.renderer.removeStyle(sidebarElement, 'transition');
        }
        this.renderer.setStyle(
          sidebarElement,
          'transform',
          `translateY(${translateY})`,
        );
      }
    }
  }

  public onDragStart(event: TouchEvent | MouseEvent): void {
    this.isDragging = true;
    this.startY = this.getY(event);
    this.startTime = performance.now();
    this.document.addEventListener('mousemove', this.onDragMove.bind(this));
    this.document.addEventListener('mouseup', this.onDragEnd.bind(this));
    this.document.addEventListener('touchmove', this.onDragMove.bind(this));
    this.document.addEventListener('touchend', this.onDragEnd.bind(this));
  }

  public onDragMove(event: TouchEvent | MouseEvent): void {
    if (!this.isDragging) {
      return;
    }
    const sidebarElement: HTMLElement | undefined =
      this.sidebar()?.nativeElement;
    if (!sidebarElement) {
      return;
    }
    this.renderer.setStyle(sidebarElement, 'transition', 'none');

    this.currentY = this.getY(event);
    const diff: number = this.startY - this.currentY;
    const dragLimit: number = this.isOpen
      ? this.topLimit
      : this.calcHeaderHeight();
    let translateY: number = dragLimit - (diff / window.innerHeight) * 100;
    translateY = Math.min(
      this.calcHeaderHeight(),
      Math.max(this.topLimit, translateY),
    );

    this.renderer.setStyle(
      sidebarElement,
      'transform',
      `translateY(${translateY}%)`,
    );
  }

  public onDragEnd(): void {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;

    const elapsedTime: number = performance.now() - this.startTime;
    const diff: number = this.startY - this.currentY;
    const swipeSpeed: number = Math.abs(diff / elapsedTime);
    const goingUp: boolean = diff > 0;
    const threshold: number = window.innerHeight / 3;
    const result: boolean = Math.abs(diff) > threshold;

    if (result || swipeSpeed > 0.3) {
      if (goingUp) {
        this.sidebarLayoutService.openSidebar();
      } else {
        this.sidebarLayoutService.closeSidebar();
      }
    }
    this.updateSidebarMobile(true);
    this.removeEventListeners();
  }

  private getY(event: TouchEvent | MouseEvent): number {
    return event instanceof MouseEvent
      ? event.clientY
      : event.touches[0].clientY;
  }

  public dismissSidebar(): void {
    this.sidebarLayoutService.closeSidebar();
  }

  private removeEventListeners(): void {
    this.document.removeEventListener('mousemove', this.onDragMove.bind(this));
    this.document.removeEventListener('mouseup', this.onDragEnd.bind(this));
    this.document.removeEventListener('touchmove', this.onDragMove.bind(this));
    this.document.removeEventListener('touchend', this.onDragEnd.bind(this));
  }

  public ngOnDestroy(): void {
    this.removeEventListeners();
  }
}
